import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { WorklistRow } from '@/components/today/WorklistRow';
import { mockPatients } from '@/data/mock';
import type { Patient } from '@/lib/types';

type StateFilter = 'all' | 'pending' | 'attended';
type ShiftFilter = 'all' | 'morning' | 'afternoon';

interface SlotEntry {
  patient: Patient;
  slot: string;
  hour: number;
  shift: 'morning' | 'afternoon';
}

function isStateFilter(v: string | null): v is StateFilter {
  return v === 'all' || v === 'pending' || v === 'attended';
}
function isShiftFilter(v: string | null): v is ShiftFilter {
  return v === 'all' || v === 'morning' || v === 'afternoon';
}

function buildSlots(patients: Patient[]): SlotEntry[] {
  // Only patients with an appointment make it onto today's worklist.
  const list = patients
    .filter((p) => p.appointmentState !== undefined)
    .sort((a, b) => {
      // Atendidas first (already seen, scrolled past), then programadas in the
      // morning of the day. Same secondary order: by status severity.
      const order = { atendida: 0, programado: 1 } as const;
      const sa = order[a.appointmentState ?? 'programado'];
      const sb = order[b.appointmentState ?? 'programado'];
      if (sa !== sb) return sa - sb;
      const sev = { crit: 0, warn: 1, ok: 2 } as const;
      return sev[a.status] - sev[b.status];
    });

  return list.map((patient, i) => {
    const startMinutes = 8 * 60 + i * 15; // 8:00 + 15-min slots
    const hour = Math.floor(startMinutes / 60);
    const minute = startMinutes % 60;
    const slot = `${hour}:${String(minute).padStart(2, '0')}`;
    const shift: 'morning' | 'afternoon' = hour < 13 ? 'morning' : 'afternoon';
    return { patient, slot, hour, shift };
  });
}

export function Today() {
  const [searchParams, setSearchParams] = useSearchParams();
  const stateFilter: StateFilter = isStateFilter(searchParams.get('state'))
    ? (searchParams.get('state') as StateFilter)
    : 'all';
  const shiftFilter: ShiftFilter = isShiftFilter(searchParams.get('shift'))
    ? (searchParams.get('shift') as ShiftFilter)
    : 'all';

  const setParam = (key: string, value: string, defaultValue: string) => {
    const params = new URLSearchParams(searchParams);
    if (value === defaultValue) params.delete(key);
    else params.set(key, value);
    setSearchParams(params, { replace: true });
  };

  const allSlots = useMemo(() => buildSlots(mockPatients), []);

  const stats = useMemo(() => {
    const total = allSlots.length;
    const attended = allSlots.filter((e) => e.patient.appointmentState === 'atendida').length;
    const pending = total - attended;
    const morning = allSlots.filter((e) => e.shift === 'morning').length;
    const afternoon = allSlots.filter((e) => e.shift === 'afternoon').length;
    const crit = allSlots.filter((e) => e.patient.status === 'crit').length;
    return { total, attended, pending, morning, afternoon, crit };
  }, [allSlots]);

  const visible = useMemo(() => {
    return allSlots.filter((e) => {
      if (stateFilter === 'pending' && e.patient.appointmentState !== 'programado') return false;
      if (stateFilter === 'attended' && e.patient.appointmentState !== 'atendida') return false;
      if (shiftFilter === 'morning' && e.shift !== 'morning') return false;
      if (shiftFilter === 'afternoon' && e.shift !== 'afternoon') return false;
      return true;
    });
  }, [allSlots, stateFilter, shiftFilter]);

  return (
    <main>
      <div className="pane-content appear">
        <header className="mb-4">
          <p className="pane-eyebrow tnum">Miércoles, 6 de mayo de 2026</p>
          <h1 className="pane-title">Buenos días, Doctor.</h1>
          <p className="pane-summary">
            {stats.total} pacientes hoy · {stats.attended}{' '}
            {stats.attended === 1 ? 'atendida' : 'atendidas'}.
          </p>
        </header>

        <section className="grid grid-cols-5 gap-2 mb-4 max-[880px]:grid-cols-3 max-[760px]:grid-cols-2">
          <StatCell label="Total hoy" value={stats.total} />
          <StatCell label="Atendidas" value={stats.attended} sub={`${pct(stats.attended, stats.total)}%`} />
          <StatCell label="Pendientes" value={stats.pending} sub={`${pct(stats.pending, stats.total)}%`} />
          <StatCell label="Mañana" value={stats.morning} sub="antes 13:00" />
          <StatCell label="Tarde" value={stats.afternoon} sub="13:00 en adelante" />
        </section>

        <section className="flex items-center gap-2 flex-wrap mb-4">
          <FilterGroup label="Estado">
            <FilterChip
              active={stateFilter === 'all'}
              onClick={() => setParam('state', 'all', 'all')}
              label="Todas"
              count={stats.total}
            />
            <FilterChip
              active={stateFilter === 'pending'}
              onClick={() => setParam('state', 'pending', 'all')}
              label="Pendientes"
              count={stats.pending}
            />
            <FilterChip
              active={stateFilter === 'attended'}
              onClick={() => setParam('state', 'attended', 'all')}
              label="Atendidas"
              count={stats.attended}
            />
          </FilterGroup>
          <span className="w-px h-6 bg-line mx-1 max-[880px]:hidden" />
          <FilterGroup label="Turno">
            <FilterChip
              active={shiftFilter === 'all'}
              onClick={() => setParam('shift', 'all', 'all')}
              label="Todo el día"
              count={stats.total}
            />
            <FilterChip
              active={shiftFilter === 'morning'}
              onClick={() => setParam('shift', 'morning', 'all')}
              label="Mañana"
              count={stats.morning}
            />
            <FilterChip
              active={shiftFilter === 'afternoon'}
              onClick={() => setParam('shift', 'afternoon', 'all')}
              label="Tarde"
              count={stats.afternoon}
            />
          </FilterGroup>
        </section>

        {visible.length > 0 ? (
          <div className="glass-panel appear-stagger">
            {visible.map(({ patient, slot }) => (
              <WorklistRow key={patient.id} patient={patient} slot={slot} />
            ))}
          </div>
        ) : (
          <div className="glass-panel py-10 text-center text-muted text-[13px]">
            No hay pacientes que coincidan con los filtros seleccionados.
          </div>
        )}
      </div>
    </main>
  );
}

function pct(part: number, total: number): number {
  if (total <= 0) return 0;
  return Math.round((part / total) * 100);
}

function StatCell({ label, value, sub }: { label: string; value: number; sub?: string }) {
  return (
    <div className="glass-panel px-4 py-3 flex flex-col gap-0.5">
      <span className="text-[10.5px] font-semibold tracking-[0.06em] uppercase text-muted">
        {label}
      </span>
      <span className="text-[22px] font-bold tracking-[-0.025em] tnum leading-[1.1] text-primary">
        {value}
      </span>
      {sub && (
        <span className="text-[11px] text-tertiary tnum">{sub}</span>
      )}
    </div>
  );
}

interface FilterGroupProps {
  label: string;
  children: React.ReactNode;
}

function FilterGroup({ label, children }: FilterGroupProps) {
  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      <span className="text-[10.5px] font-semibold tracking-[0.06em] uppercase text-muted mr-1">
        {label}
      </span>
      {children}
    </div>
  );
}

interface FilterChipProps {
  label: string;
  count: number;
  active: boolean;
  onClick: () => void;
}

function FilterChip({ label, count, active, onClick }: FilterChipProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 text-[12px] font-medium px-2.5 py-1.5 rounded-md border cursor-pointer transition-colors ${
        active
          ? 'bg-primary text-surface border-primary'
          : 'bg-transparent text-secondary border-line hover:bg-soft hover:border-line-default hover:text-primary'
      }`}
    >
      <span>{label}</span>
      <span className={`text-[10.5px] font-semibold tnum ${active ? 'opacity-80' : 'text-muted'}`}>
        {count}
      </span>
    </button>
  );
}
